import {
  menu,
  type MenuItem,
  type MenuMsg,
  type MenuParts,
  type MenuState,
} from '@llui/components/menu';
import { component, div, island, type Mountable, type Renderable } from '@llui/dom';

/** A standard LLui menu with DungeonLogs render slots for trigger and items. */
export interface DropdownMenuOptions {
  /** Stable id base for trigger/content/item ARIA relationships. */
  id: string;
  /** LLui's serialisable menu item tree. */
  items: MenuItem[];
  /** Render the trigger using LLui's complete connected part bag. */
  trigger: (parts: MenuParts['trigger']) => Mountable;
  /** Render the menu body; item elements must spread `parts.item(value).item`. */
  children: (parts: MenuParts) => Renderable;
  /** Called for mouse and keyboard item activation. */
  onSelect: (value: string) => void;
  /** Optional notification for Escape, outside click, or trigger-close. */
  onDismiss?: () => void;
  /** App-specific layout extension on LLui's menu content. */
  panelClass?: string;
  /** Forwarded `data-testid` on LLui's menu content. */
  testid?: string;
  /** Floating placement. Defaults to `bottom-start`. */
  placement?: NonNullable<Parameters<typeof menu.overlay>[0]['placement']>;
  /** Gap between trigger and content. Defaults to 4px. */
  offset?: number;
}

type DropdownMenuEffect =
  | { type: 'select'; value: string }
  | { type: 'dismiss' };

function findItem(items: MenuItem[], value: string): MenuItem | undefined {
  for (const item of items) {
    if (item.value === value) return item;
    if (item.children) {
      const found = findItem(item.children, value);
      if (found) return found;
    }
  }
  return undefined;
}

function selectedValue(state: MenuState, msg: MenuMsg): string | null {
  const value =
    msg.type === 'select'
      ? msg.value
      : msg.type === 'selectHighlighted'
        ? (state.highlights[msg.level] ?? null)
        : null;
  if (value === null) return null;
  const item = findItem(state.items, value);
  return item?.kind === 'action' && !item.disabled && !item.children ? value : null;
}

export function dropdownMenu(opts: DropdownMenuOptions): Mountable {
  const initialState = menu.init({ items: opts.items });

  const def = component<MenuState, MenuMsg, DropdownMenuEffect>({
    name: `DropdownMenu(${opts.id})`,
    init: () => [initialState, []],
    update: (state, msg) => {
      const selected = selectedValue(state, msg);
      const [next] = menu.update(state, msg);
      const effects: DropdownMenuEffect[] = selected
        ? [{ type: 'select', value: selected }]
        : state.open && !next.open
          ? [{ type: 'dismiss' }]
          : [];
      return [next, effects];
    },
    view: ({ state, send }) => {
      const parts = menu.connect(state, send, { id: opts.id });
      const panelClass = `dl-menu-panel${opts.panelClass ? ` ${opts.panelClass}` : ''}`;

      return [
        opts.trigger(parts.trigger),
        menu.overlay({
          state,
          send,
          parts,
          positionerClass: 'dl-menu',
          placement: opts.placement ?? 'bottom-start',
          offset: opts.offset ?? 4,
          content: () => [
            div(
              {
                ...parts.content,
                class: panelClass,
                'data-testid': opts.testid,
              },
              opts.children(parts),
            ),
          ],
        }),
      ];
    },
    onEffect: (effect) => {
      if (effect.type === 'select') opts.onSelect(effect.value);
      else opts.onDismiss?.();
    },
  });

  return div({ class: 'dl-menu-boundary' }, [
    island({
      def,
      initialState,
      reason: 'LLui owns the complete menu state, trigger, focus, keyboard, and overlay lifecycle.',
    }),
  ]);
}
